import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Folder,
  Globe2,
  Loader2,
  MapPinOff,
  Move,
  RotateCcw,
  Star,
  StarOff,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import {
  MEDIA_FOLDERS,
  type AdminMediaResponse,
  type MediaBulkAction,
  type MediaFolder,
} from "@ckgo2/shared/types";
import { chunk } from "@ckgo2/shared/utils";
import { api, type ApiResponse } from "@/api/client";
import DestinationPicker from "@/components/common/DestinationPicker";
import ErrorBanner from "@/components/common/ErrorBanner";
import { MEDIA_SOURCES_KEY, useMediaSources } from "@/hooks/useMediaSources";
import { mediaFolderLabel } from "@/lib/mediaFormat";
import AddToAlbumModal from "./AddToAlbumModal";
import type { MediaSelection } from "./useMediaSelection";

/**
 * Lišta hromadných akcí stránky Média (řez 03, Kontrakt bod 6).
 *
 * Rodič ji vykreslí jen při `selection.selected.size > 0`. `onDone` lišta volá po každém
 * úspěšném zápisu a rodič v něm jen obnoví data (zneplatní `MEDIA_LIST_KEY`, alba
 * `["media-folders"]` a `MEDIA_SOURCES_KEY`); výběr v `onDone` nemění, výběr spravuje lišta
 * (po akci ho vyčistí sama, po obnově z koše nechá vybrané ty, které se obnovit nepodařilo).
 * V `trashMode` lišta obnovuje jen položky s `liveTwinId === null`, dvojčata přeskočí a hlásí.
 *
 * Strop výběru lišta nemá (vize nezavádí nový ochranný mechanismus): na dávky dělí API.
 * Kroky po úspěchu jsou v `onSuccess` options `useMutation`, ne v `mutate(…, { onSuccess })`:
 * `selection.clear()` lištu odpojí a zpětná volání `mutate` by po odpojení TanStack nezavolal.
 */
export interface MediaBulkBarProps {
  selection: MediaSelection;
  /** Načtené fotky; z nich lišta počítá, kolik vybraných je použitých. */
  items: readonly AdminMediaResponse[];
  trashMode: boolean;
  onDone: () => void;
}

export type BulkActionKey =
  | "addCountries"
  | "removeCountries"
  | "setSource"
  | "star"
  | "unstar"
  | "album"
  | "move"
  | "trash"
  | "restore";

/** Akce lišty mimo koš v pořadí Tvaru UI (bod 4); v koši jen `restore`. */
export const ACTIVE_BULK_ACTIONS: readonly BulkActionKey[] = [
  "addCountries",
  "removeCountries",
  "setSource",
  "star",
  "unstar",
  "album",
  "move",
  "trash",
];

const TRASH_BULK_ACTIONS: readonly BulkActionKey[] = ["restore"];

/** Počet obnov z koše, které běží souběžně (prohlížeč drží ~6 spojení na hostitele). */
const RESTORE_GROUP_SIZE = 6;

/**
 * Kolik vybraných fotek je použitých (`usageCount > 0`). Počítá jen načtené položky: výběr
 * vzniká jen z načtených dlaždic, ID mimo `items` se nepočítá.
 */
export function usedSelectedCount(
  selected: ReadonlySet<string>,
  items: readonly AdminMediaResponse[],
): number {
  let used = 0;
  for (const item of items) if (selected.has(item.id) && item.usageCount > 0) used += 1;
  return used;
}

/**
 * Rozdělení výběru v koši: `twins` = fotky, jejichž otisk má živá fotka (API by vrátilo
 * 409 `media_restore_live_twin`), se neposílají; ostatní včetně ID mimo načtené rozhodne API.
 */
export function restorePlan(
  selected: ReadonlySet<string>,
  items: readonly AdminMediaResponse[],
): { restorable: string[]; twins: string[] } {
  const byId = new Map(items.map((item) => [item.id, item]));
  const restorable: string[] = [];
  const twins: string[] = [];
  for (const id of selected) {
    const twin = byId.get(id)?.liveTwinId ?? null;
    (twin === null ? restorable : twins).push(id);
  }
  return { restorable, twins };
}

const MEDIA_DETAIL_KEY = ["media"] as const;
const MEDIA_FOLDERS_KEY = ["media-folders"] as const;
const MEDIA_ALBUM_MEMBERSHIP_KEY = ["media-album-membership"] as const;

/** Zápis lišty a jeho vstup: země jen u zemí, zdroj jen u zdroje, druh jen u přesunu. */
export type BulkWriteOp =
  | { kind: "addCountries" | "removeCountries"; countryIds: readonly string[] }
  | { kind: "setSource"; source: string }
  | { kind: "star" | "unstar" }
  | { kind: "move"; folder: MediaFolder }
  | { kind: "trash" };

/** Jeden hromadný zápis (`POST /api/admin/upload/bulk`): tělo a klíče, které se po úspěchu
 *  zneplatní navíc k detailu. */
export interface BulkWrite {
  body: MediaBulkAction;
  keys: readonly QueryKey[];
}

/**
 * Popis zápisu pro vybrané `ids`. Tělo odpovídá `MediaBulkActionSchema`; prázdný zdroj (po
 * oříznutí) zdroj smaže (`null`).
 */
export function bulkWrite(ids: readonly string[], op: BulkWriteOp): BulkWrite {
  const list = [...ids];
  switch (op.kind) {
    case "addCountries":
    case "removeCountries":
      return { body: { action: op.kind, ids: list, countryIds: [...op.countryIds] }, keys: [] };
    case "setSource": {
      const source = op.source.trim();
      return {
        body: { action: "setSource", ids: list, source: source === "" ? null : source },
        keys: [MEDIA_SOURCES_KEY],
      };
    }
    case "star":
    case "unstar":
      return {
        body: { action: "setStarred", ids: list, isStarred: op.kind === "star" },
        keys: [],
      };
    case "move":
      return { body: { action: "setFolder", ids: list, folder: op.folder }, keys: [] };
    case "trash":
      return { body: { action: "trash", ids: list }, keys: [] };
  }
}

/**
 * Obnova z koše po skupinách (`RESTORE_GROUP_SIZE` souběžně, skupiny za sebou). Selhání
 * jedné fotky ostatní nezastaví (`allSettled`); s prázdným seznamem neodejde nic.
 */
export async function restoreInGroups(
  ids: readonly string[],
  restoreOne: (id: string) => Promise<unknown>,
  groupSize = RESTORE_GROUP_SIZE,
): Promise<{ restored: string[]; failed: string[] }> {
  const restored: string[] = [];
  const failed: string[] = [];
  for (const group of chunk(ids, groupSize)) {
    const results = await Promise.allSettled(group.map((id) => restoreOne(id)));
    results.forEach((result, i) =>
      (result.status === "fulfilled" ? restored : failed).push(group[i]),
    );
  }
  return { restored, failed };
}

/**
 * Co udělat s výběrem po doběhu obnovy z koše. `selectionUnchanged` = lišta je pořád
 * připojená a výběr je tentýž jako při spuštění; jinak (admin mezitím přepnul koš, změnil
 * filtr nebo výběr) se výběr nechá, jak je, a hlášení se neukáže. Jinak zůstanou vybrané
 * jen přeskočená dvojčata a nezdařené, obnovené z výběru odejdou.
 */
export type RestoreFollowUp =
  | { kind: "clear" }
  | { kind: "keep"; ids: string[]; notice: RestoreNotice }
  | { kind: "leave" };

export function restoreFollowUp(
  result: { failed: readonly string[]; twins: readonly string[] },
  selectionUnchanged: boolean,
): RestoreFollowUp {
  if (!selectionUnchanged) return { kind: "leave" };
  const ids = [...result.twins, ...result.failed];
  if (ids.length === 0) return { kind: "clear" };
  return {
    kind: "keep",
    ids,
    notice: { twinsSkipped: result.twins.length, failed: result.failed.length },
  };
}

const ACTION_ICONS: Record<BulkActionKey, typeof Star> = {
  addCountries: Globe2,
  removeCountries: MapPinOff,
  setSource: Tag,
  star: Star,
  unstar: StarOff,
  album: Folder,
  move: Move,
  trash: Trash2,
  restore: RotateCcw,
};

export interface RestoreNotice {
  twinsSkipped: number;
  failed: number;
}

export interface MediaBulkBarViewProps {
  count: number;
  trashMode: boolean;
  /** Běží akce: všechna tlačítka zakázaná. */
  pending: boolean;
  /** Chyba akce bez vlastního dialogu (★, zrušit ★). */
  error: Error | null;
  /** Výsledek poslední obnovy z koše, když něco zůstalo vybrané. */
  notice: RestoreNotice | null;
  onAction: (key: BulkActionKey) => void;
  onClear: () => void;
}

export function MediaBulkBarView({
  count,
  trashMode,
  pending,
  error,
  notice,
  onAction,
  onClear,
}: MediaBulkBarViewProps): ReactElement {
  const { t } = useTranslation();
  const actions = trashMode ? TRASH_BULK_ACTIONS : ACTIVE_BULK_ACTIONS;
  const labels: Record<BulkActionKey, { label: string; title: string }> = {
    addCountries: {
      label: t("mediaBulk.actions.addCountries"),
      title: t("mediaBulk.actionTitles.addCountries"),
    },
    removeCountries: {
      label: t("mediaBulk.actions.removeCountries"),
      title: t("mediaBulk.actionTitles.removeCountries"),
    },
    setSource: {
      label: t("mediaBulk.actions.setSource"),
      title: t("mediaBulk.actionTitles.setSource"),
    },
    star: { label: t("mediaBulk.actions.star"), title: t("mediaBulk.actionTitles.star") },
    unstar: { label: t("mediaBulk.actions.unstar"), title: t("mediaBulk.actionTitles.unstar") },
    album: { label: t("mediaBulk.actions.album"), title: t("mediaBulk.actionTitles.album") },
    move: { label: t("mediaBulk.actions.move"), title: t("mediaBulk.actionTitles.move") },
    trash: { label: t("mediaBulk.actions.trash"), title: t("mediaBulk.actionTitles.trash") },
    restore: {
      label: t("mediaBulk.actions.restore"),
      title: t("mediaBulk.actionTitles.restore"),
    },
  };
  return (
    <div className="space-y-2">
      <div
        data-media-bulk=""
        data-count={count}
        className="bg-primary-50 dark:bg-primary-900/20 flex flex-wrap items-center gap-2 rounded-lg p-2"
      >
        <span className="text-primary-700 dark:text-primary-300 px-2 text-sm font-medium">
          {t("mediaBulk.selected", { count })}
        </span>
        {actions.map((key) => {
          const Icon = ACTION_ICONS[key];
          return (
            <button
              key={key}
              type="button"
              data-media-bulk-action={key}
              title={labels[key].title}
              onClick={() => onAction(key)}
              disabled={pending}
              className={`btn-ghost text-sm ${key === "trash" ? "text-error-600 hover:bg-error-50" : ""}`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" /> {labels[key].label}
            </button>
          );
        })}
        {pending && <Loader2 className="text-primary-500 h-4 w-4 animate-spin" />}
        <button
          type="button"
          onClick={onClear}
          disabled={pending}
          aria-label={t("mediaBulk.clearSelection")}
          title={t("mediaBulk.clearSelection")}
          className="btn-ghost ml-auto text-sm"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {notice && (
        <div
          role="status"
          data-media-bulk-notice=""
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-300"
        >
          {notice.twinsSkipped > 0 && (
            <p>{t("mediaBulk.restore.twinsSkipped", { count: notice.twinsSkipped })}</p>
          )}
          {notice.failed > 0 && <p>{t("mediaBulk.restore.failed", { count: notice.failed })}</p>}
        </div>
      )}
      <ErrorBanner error={error} />
    </div>
  );
}

interface BulkDialogFrameProps {
  titleId: string;
  title: string;
  /** Atributy `data-*` panelu dialogu (selektory testů a E2E). */
  dataAttrs: Record<`data-${string}`, string | number>;
  error: Error | null;
  pending: boolean;
  confirmLabel: string;
  confirmClass: "btn-primary" | "btn-danger";
  confirmDisabled?: boolean;
  /** Výstražná ikona vedle nadpisu (potvrzení smazání). */
  danger?: boolean;
  /**
   * `form` = panel je `<form>` a potvrzení `submit` (Enter v poli odešle); jinak potvrzení
   * volá `onConfirm` klikem (pickery s vlastním Enterem).
   */
  as: "form" | "div";
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}

/** Společný rám dialogů lišty: překryv, panel s nadpisem, tělo, chyba a patička Zrušit + potvrzení. */
function BulkDialogFrame({
  titleId,
  title,
  dataAttrs,
  error,
  pending,
  confirmLabel,
  confirmClass,
  confirmDisabled = false,
  danger = false,
  as,
  onConfirm,
  onClose,
  children,
}: BulkDialogFrameProps): ReactElement {
  const { t } = useTranslation();
  const Panel = as;
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onConfirm();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Panel
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...dataAttrs}
        onSubmit={as === "form" ? handleSubmit : undefined}
        className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
      >
        <div className="flex items-start gap-4">
          {danger && (
            <div className="bg-error-100 text-error-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-4">
            <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            {children}
          </div>
        </div>
        <ErrorBanner error={error} />
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={pending}>
            {t("common.actions.cancel")}
          </button>
          <button
            type={as === "form" ? "submit" : "button"}
            onClick={as === "form" ? undefined : onConfirm}
            className={confirmClass}
            disabled={pending || confirmDisabled}
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </Panel>
    </div>
  );
}

export interface DeleteConfirmViewProps {
  /** Počet vybraných. */
  total: number;
  /** Počet vybraných s `usageCount > 0`. */
  used: number;
  pending: boolean;
  error: Error | null;
  uid: string;
  onConfirm: () => void;
  onClose: () => void;
}

/** Potvrzení smazání do koše s počtem použitých vybraných (vzhled `ConfirmModal`). */
export function DeleteConfirmView({
  total,
  used,
  pending,
  error,
  uid,
  onConfirm,
  onClose,
}: DeleteConfirmViewProps): ReactElement {
  const { t } = useTranslation();
  return (
    <BulkDialogFrame
      titleId={`${uid}-title`}
      title={t("mediaBulk.confirmDelete.title", { count: total })}
      dataAttrs={{ "data-media-confirm-delete": "", "data-used-count": used }}
      error={error}
      pending={pending}
      confirmLabel={t("mediaBulk.confirmDelete.confirm")}
      confirmClass="btn-danger"
      danger
      as="div"
      onConfirm={onConfirm}
      onClose={onClose}
    >
      <p className="text-sm text-gray-600 dark:text-gray-400">
        {used > 0
          ? t("mediaBulk.confirmDelete.used", { count: used, total })
          : t("mediaBulk.confirmDelete.noneUsed")}
      </p>
    </BulkDialogFrame>
  );
}

type CountryAction = "addCountries" | "removeCountries";

/** Dialog přiřazení / odebrání zemí: jen zemská úroveň, Uložit až s aspoň jednou zemí. */
function CountriesDialog({
  action,
  count,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  action: CountryAction;
  count: number;
  pending: boolean;
  error: Error | null;
  onSubmit: (countryIds: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const [countryIds, setCountryIds] = useState<string[]>([]);
  return (
    <BulkDialogFrame
      titleId={`${uid}-title`}
      title={
        action === "addCountries"
          ? t("mediaBulk.countries.addTitle", { count })
          : t("mediaBulk.countries.removeTitle", { count })
      }
      dataAttrs={{ "data-media-bulk-dialog": action }}
      error={error}
      pending={pending}
      confirmLabel={t("mediaBulk.countries.save")}
      confirmClass="btn-primary"
      confirmDisabled={countryIds.length === 0}
      as="div"
      onConfirm={() => onSubmit(countryIds)}
      onClose={onClose}
    >
      <div>
        <label htmlFor={`${uid}-countries`} className="label">
          {t("mediaBulk.countries.label")}
        </label>
        <DestinationPicker
          id={`${uid}-countries`}
          mode="multiple"
          level="country"
          value={countryIds}
          onChange={setCountryIds}
          disabled={pending}
        />
      </div>
    </BulkDialogFrame>
  );
}

/**
 * Dialog zdroje: jedno pole s nabídkou dřívějších hodnot. Dotaz na zdroje běží až po
 * otevření (komponenta je připojená jen s otevřeným dialogem). Prázdné pole zdroj smaže.
 */
function SourceDialog({
  count,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  count: number;
  pending: boolean;
  error: Error | null;
  onSubmit: (source: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const { data: sources = [] } = useMediaSources();
  const [source, setSource] = useState("");
  return (
    <BulkDialogFrame
      titleId={`${uid}-title`}
      title={t("mediaBulk.source.title", { count })}
      dataAttrs={{ "data-media-bulk-dialog": "setSource" }}
      error={error}
      pending={pending}
      confirmLabel={t("mediaBulk.source.save")}
      confirmClass="btn-primary"
      as="form"
      onConfirm={() => onSubmit(source)}
      onClose={onClose}
    >
      <div>
        <label htmlFor={`${uid}-source`} className="label">
          {t("mediaBulk.source.label")}
        </label>
        <input
          id={`${uid}-source`}
          name="source"
          type="text"
          autoFocus
          maxLength={120}
          list={`${uid}-sources`}
          value={source}
          onChange={(e) => setSource(e.target.value)}
          aria-describedby={`${uid}-hint`}
          className="input w-full"
        />
        <datalist id={`${uid}-sources`}>
          {sources.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <p id={`${uid}-hint`} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t("mediaBulk.source.hint")}
        </p>
      </div>
    </BulkDialogFrame>
  );
}

/**
 * Dialog přesunu do druhu. Chyba přesunu se ukáže v dialogu (dialog při chybě zůstane
 * otevřený), ne v liště pod překryvem.
 */
function MoveDialog({
  count,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  count: number;
  pending: boolean;
  error: Error | null;
  onSubmit: (folder: MediaFolder) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const [folder, setFolder] = useState<MediaFolder>("general");
  return (
    <BulkDialogFrame
      titleId={`${uid}-title`}
      title={t("media.move.title", { count })}
      dataAttrs={{ "data-media-bulk-dialog": "move" }}
      error={error}
      pending={pending}
      confirmLabel={t("media.move.action")}
      confirmClass="btn-primary"
      as="form"
      onConfirm={() => onSubmit(folder)}
      onClose={onClose}
    >
      <select
        aria-labelledby={`${uid}-title`}
        value={folder}
        onChange={(e) => setFolder(e.target.value as MediaFolder)}
        disabled={pending}
        className="input w-full"
      >
        {MEDIA_FOLDERS.map((f) => (
          <option key={f} value={f}>
            {mediaFolderLabel(f)}
          </option>
        ))}
      </select>
    </BulkDialogFrame>
  );
}

type BulkDialog = CountryAction | "setSource" | "album" | "move" | "trash";

export default function MediaBulkBar({
  selection,
  items,
  trashMode,
  onDone,
}: MediaBulkBarProps): ReactElement | null {
  const queryClient = useQueryClient();
  const uid = useId();
  const [dialog, setDialog] = useState<BulkDialog | null>(null);
  const [notice, setNotice] = useState<RestoreNotice | null>(null);
  const { selected, clear, selectAll } = selection;
  const ids = [...selected];

  // Obnova z koše běží desítky sekund a filtry i dlaždice zůstávají aktivní: po doběhu se
  // výběr mění, jen když je lišta připojená a výběr je tentýž (`restoreFollowUp`).
  const mountedRef = useRef(false);
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const invalidate = (keys: readonly QueryKey[]) => {
    for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
  };
  /** Po úspěchu zápisu: zavřít dialog, zneplatnit dotčené klíče, rodič obnoví seznam, výběr pryč. */
  const finish = (keys: readonly QueryKey[]) => {
    setDialog(null);
    invalidate([MEDIA_DETAIL_KEY, ...keys]);
    onDone();
    clear();
  };

  const writeMutation = useMutation({
    mutationFn: (write: BulkWrite) =>
      api.post<ApiResponse<unknown>>("/api/admin/upload/bulk", write.body),
    onSuccess: (_res, write) => finish(write.keys),
  });
  const restoreMutation = useMutation({
    mutationFn: async (plan: {
      restorable: string[];
      twins: string[];
      from: ReadonlySet<string>;
    }) => {
      const result = await restoreInGroups(plan.restorable, (id) =>
        api.post(`/api/admin/upload/${encodeURIComponent(id)}/restore`, {}),
      );
      return { ...result, twins: plan.twins, from: plan.from };
    },
    onSuccess: (result) => {
      if (result.restored.length > 0) {
        invalidate([MEDIA_DETAIL_KEY]);
        onDone();
      }
      const unchanged = mountedRef.current && selectedRef.current === result.from;
      const next = restoreFollowUp(result, unchanged);
      if (next.kind === "leave") return;
      clear();
      if (next.kind === "keep") {
        selectAll(next.ids);
        setNotice(next.notice);
      }
    },
  });

  if (selected.size === 0) return null;

  const pending = writeMutation.isPending || restoreMutation.isPending;

  const openDialog = (next: BulkDialog | null) => {
    writeMutation.reset();
    setDialog(next);
  };
  const write = (op: BulkWriteOp) => writeMutation.mutate(bulkWrite(ids, op));

  const onAction = (key: BulkActionKey) => {
    setNotice(null);
    switch (key) {
      case "star":
      case "unstar":
        openDialog(null);
        write({ kind: key });
        return;
      case "restore":
        restoreMutation.mutate({ ...restorePlan(selected, items), from: selected });
        return;
      default:
        openDialog(key);
    }
  };

  const count = selected.size;
  const dialogError = writeMutation.error;

  return (
    <>
      <MediaBulkBarView
        count={count}
        trashMode={trashMode}
        pending={pending}
        error={dialog === null ? writeMutation.error : null}
        notice={notice}
        onAction={onAction}
        onClear={clear}
      />
      {(dialog === "addCountries" || dialog === "removeCountries") && (
        <CountriesDialog
          action={dialog}
          count={count}
          pending={writeMutation.isPending}
          error={dialogError}
          onSubmit={(countryIds) => write({ kind: dialog, countryIds })}
          onClose={() => openDialog(null)}
        />
      )}
      {dialog === "setSource" && (
        <SourceDialog
          count={count}
          pending={writeMutation.isPending}
          error={dialogError}
          onSubmit={(source) => write({ kind: "setSource", source })}
          onClose={() => openDialog(null)}
        />
      )}
      {dialog === "trash" && (
        <DeleteConfirmView
          total={count}
          used={usedSelectedCount(selected, items)}
          pending={writeMutation.isPending}
          error={dialogError}
          uid={uid}
          onConfirm={() => write({ kind: "trash" })}
          onClose={() => openDialog(null)}
        />
      )}
      {dialog === "move" && (
        <MoveDialog
          count={count}
          pending={writeMutation.isPending}
          error={dialogError}
          onSubmit={(folder) => write({ kind: "move", folder })}
          onClose={() => openDialog(null)}
        />
      )}
      {dialog === "album" && (
        <AddToAlbumModal
          mediaIds={ids}
          onClose={() => openDialog(null)}
          onDone={() => finish([MEDIA_FOLDERS_KEY, MEDIA_ALBUM_MEMBERSHIP_KEY])}
        />
      )}
    </>
  );
}
